import { ActionIcon, Group, SegmentedControl, Slider, Tooltip } from '@mantine/core';
import { IconTextDecrease, IconTextIncrease, IconTypography } from '@tabler/icons-react';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';

export type FontSizeSwitcherVariant = 'icons' | 'segmented' | 'slider';

export interface FontSizeSwitcherProps {
  variant?: FontSizeSwitcherVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const fontSizes = ['xs', 'sm', 'regular', 'lg', 'xl'] as const;
export type FontSize = (typeof fontSizes)[number];

const FontSizeSwitcher = forwardRef<HTMLDivElement, FontSizeSwitcherProps>(
  ({ variant = 'segmented', size = 'sm', showLabel = false }, ref) => {
    const fontSize = usePreferencesStore((state) => state.fontSize);
    const setFontSize = usePreferencesStore((state) => state.setFontSize);

    const handleIncrease = () => {
      const currentIndex = fontSizes.indexOf(fontSize);
      if (currentIndex < fontSizes.length - 1) {
        setFontSize(fontSizes[currentIndex + 1]);
      }
    };

    const handleDecrease = () => {
      const currentIndex = fontSizes.indexOf(fontSize);
      if (currentIndex > 0) {
        setFontSize(fontSizes[currentIndex - 1]);
      }
    };

    if (variant === 'icons') {
      return (
        <Group ref={ref} gap="xs">
          <Tooltip label="Decrease font size">
            <ActionIcon
              variant="subtle"
              size={size}
              onClick={handleDecrease}
              disabled={fontSize === 'xs'}
            >
              <IconTextDecrease size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={`Current: ${fontSize}`}>
            <ActionIcon variant="light" size={size}>
              <IconTypography size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Increase font size">
            <ActionIcon
              variant="subtle"
              size={size}
              onClick={handleIncrease}
              disabled={fontSize === 'xl'}
            >
              <IconTextIncrease size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    }

    if (variant === 'slider') {
      const sliderValue = fontSizes.indexOf(fontSize as FontSize);
      return (
        <Slider
          ref={ref}
          value={sliderValue}
          onChange={(value) => setFontSize(fontSizes[value] as FontSize)}
          min={0}
          max={fontSizes.length - 1}
          step={1}
          marks={[
            { value: 0, label: showLabel ? 'Small' : 'A' },
            { value: 1, label: showLabel ? 'S+' : 'A' },
            { value: 2, label: showLabel ? 'Regular' : 'A' },
            { value: 3, label: showLabel ? 'L' : 'A' },
            { value: 4, label: showLabel ? 'XL' : 'A' },
          ]}
          size={size}
          style={{ minWidth: 120 }}
        />
      );
    }

    return (
      <SegmentedControl
        ref={ref}
        value={fontSize}
        onChange={(value) => setFontSize(value as FontSize)}
        size={size}
        data={[
          {
            value: 'small',
            label: (
              <Group gap={4} justify="center">
                <IconTypography size={12} />
            {showLabel && <span>Small</span>}
          </Group>
        ),
      },
      {
        value: 'regular',
        label: (
          <Group gap={4} justify="center">
            <IconTypography size={16} />
            {showLabel && <span>Regular</span>}
          </Group>
        ),
      },
      {
        value: 'xl',
        label: (
          <Group gap={4} justify="center">
            <IconTypography size={20} />
            {showLabel && <span>Large</span>}
          </Group>
        ),
      },
    ]}
  />
    );
  }
);

FontSizeSwitcher.displayName = 'FontSizeSwitcher';

export default FontSizeSwitcher;
